/**
 * NeedsTab — Design compact PRO (refonte fév. 2026).
 *
 * Principes UX :
 *  - Barre du haut fine et unifiée (titre + KPI + CTA principal + menu d'actions secondaires).
 *  - Tabs de statuts (avec compteurs) — remplace filtre statut + badges en double.
 *  - Filtre "Espace" en pills scrollables (rapide, visible).
 *  - Cartes denses en 2 lignes utiles avec actions dans un menu contextuel.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, Trash2, Send, ClipboardList, Flame, CheckCircle, X, Edit2, MoreVertical,
  Home, Gamepad2, TreePine, UtensilsCrossed, Droplets, Package, ArrowRightCircle,
  FileText, FileSpreadsheet, Warehouse, Filter, SlidersHorizontal, ChevronDown, ChevronUp,
} from "lucide-react";
import ExpenseAnalysisBadges from "./ExpenseAnalysisBadges";
import NeedToStockDialog from "./NeedToStockDialog";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const formatPrice = (p) => new Intl.NumberFormat("fr-FR").format(Math.round(p || 0));

const LOCATIONS = [
  { value: "salle", label: "Salle", icon: Home, dot: "bg-blue-400" },
  { value: "salle_jeux", label: "Jeux", icon: Gamepad2, dot: "bg-fuchsia-400" },
  { value: "jardin", label: "Jardin", icon: TreePine, dot: "bg-emerald-400" },
  { value: "cuisine", label: "Cuisine", icon: UtensilsCrossed, dot: "bg-orange-400" },
  { value: "toilettes", label: "Toilettes", icon: Droplets, dot: "bg-cyan-400" },
  { value: "autres", label: "Autres", icon: Package, dot: "bg-slate-400" },
];
const LOC_MAP = Object.fromEntries(LOCATIONS.map((l) => [l.value, l]));

const STATUS_TABS = [
  { value: "en_attente", label: "En attente", accent: "text-amber-300", ring: "ring-amber-400/60", bg: "bg-amber-500/15" },
  { value: "traite", label: "Traités", accent: "text-emerald-300", ring: "ring-emerald-400/60", bg: "bg-emerald-500/15" },
  { value: "annule", label: "Annulés", accent: "text-rose-300", ring: "ring-rose-400/60", bg: "bg-rose-500/15" },
  { value: "all", label: "Tous", accent: "text-slate-200", ring: "ring-slate-400/60", bg: "bg-slate-500/15" },
  { value: "archived", label: "Archives", accent: "text-slate-300", ring: "ring-slate-500/60", bg: "bg-slate-700/40" },
];

const emptyNewItem = { location: "cuisine", description: "", quantity: 1, unit_price: 0, notes: "" };

const NeedsTab = ({ currentUser }) => {
  const [needs, setNeeds] = useState([]);
  const [analyses, setAnalyses] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusTab, setStatusTab] = useState("en_attente");
  const [locationFilter, setLocationFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  // Creation modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNeed, setEditingNeed] = useState(null);
  const [listItems, setListItems] = useState([]);
  const [listLocation, setListLocation] = useState("cuisine");
  const [listDescription, setListDescription] = useState("");
  const [listUrgency, setListUrgency] = useState("normale");
  const [listSupplier, setListSupplier] = useState("");
  const [listNotes, setListNotes] = useState("");
  const [newItem, setNewItem] = useState(emptyNewItem);
  const [stockDialogNeed, setStockDialogNeed] = useState(null);

  const isAdmin = currentUser?.role === "admin";
  const isManager = currentUser?.role === "manager";
  const requesterName = currentUser?.name || currentUser?.username || "Responsable Op. & Log";

  const fetchNeeds = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusTab === "archived") {
        params.include_archived = true;
      } else if (statusTab !== "all") {
        params.status = statusTab;
      }
      if (locationFilter !== "all") params.location = locationFilter;
      const res = await axios.get(`${API}/needs`, { params });
      let list = res.data.needs || [];
      // For the "archived" tab, keep only archived ones
      if (statusTab === "archived") list = list.filter((n) => n.archived === true);
      setNeeds(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [statusTab, locationFilter]);

  const fetchAnalysis = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/needs/analysis`);
      const map = {};
      (res.data.analyses || []).forEach((a) => { map[a.need_id] = a; });
      setAnalyses(map);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { fetchNeeds(); }, [fetchNeeds]);
  useEffect(() => { if (isAdmin) fetchAnalysis(); }, [isAdmin, needs, fetchAnalysis]);

  // Compteurs globaux (indépendants du filtre affiché)
  const [globalCounts, setGlobalCounts] = useState({ en_attente: 0, traite: 0, annule: 0, archived: 0 });
  useEffect(() => {
    const loadCounts = async () => {
      try {
        // Fetch active (non-archived) + archived separately to have both counts
        const [activeRes, archivedRes] = await Promise.all([
          axios.get(`${API}/needs`, { params: {} }),
          axios.get(`${API}/needs`, { params: { include_archived: true } }),
        ]);
        const active = activeRes.data.needs || [];
        const all = archivedRes.data.needs || [];
        setGlobalCounts({
          en_attente: active.filter((n) => n.status === "en_attente").length,
          traite: active.filter((n) => n.status === "traite").length,
          annule: active.filter((n) => n.status === "annule").length,
          archived: all.filter((n) => n.archived === true).length,
        });
      } catch (e) { /* silent */ }
    };
    loadCounts();
  }, [needs.length]);

  const totalPending = globalCounts.en_attente;
  const totalPendingAmount = useMemo(() => {
    return needs
      .filter((n) => n.status === "en_attente")
      .reduce((s, n) => s + (n.amount || 0), 0);
  }, [needs]);

  const resetListForm = () => {
    setListItems([]);
    setListLocation("cuisine");
    setListDescription("");
    setListUrgency("normale");
    setListSupplier("");
    setListNotes("");
    setNewItem(emptyNewItem);
    setEditingNeed(null);
  };

  const openCreate = () => {
    resetListForm();
    setShowCreateModal(true);
  };

  const openEdit = (need) => {
    setEditingNeed(need);
    setListItems((need.items || []).map((it, i) => ({
      id: i + Date.now(),
      location: it.location || need.location,
      description: it.description,
      quantity: it.quantity || 1,
      unit_price: it.unit_price || 0,
      amount: (it.quantity || 1) * (it.unit_price || 0),
      notes: it.notes || "",
    })));
    setListLocation(need.location || "cuisine");
    setListDescription(need.description || "");
    setListUrgency(need.urgency || "normale");
    setListSupplier(need.supplier || "");
    setListNotes(need.notes || "");
    setShowCreateModal(true);
  };

  const addItemToList = () => {
    if (!newItem.description.trim()) {
      toast.error("Description requise");
      return;
    }
    const amt = (newItem.quantity || 1) * (newItem.unit_price || 0);
    setListItems([...listItems, { ...newItem, amount: amt, id: Date.now() }]);
    setNewItem({ ...emptyNewItem, location: newItem.location });
  };

  const removeFromList = (id) => setListItems(listItems.filter((x) => x.id !== id));

  const getListTotal = () => listItems.reduce((s, it) => s + (it.amount || 0), 0);

  const saveNeed = async () => {
    if (listItems.length === 0) {
      toast.error("Ajoutez au moins un article");
      return;
    }
    const desc = listDescription.trim() || `Besoins ${LOC_MAP[listLocation]?.label || ""} - ${new Date().toLocaleDateString("fr-FR")}`;
    const payload = {
      location: listLocation,
      description: desc,
      items: listItems.map((it) => ({
        location: it.location || listLocation,
        description: it.description,
        quantity: it.quantity || 1,
        unit_price: it.unit_price || 0,
        amount: it.amount || (it.quantity || 1) * (it.unit_price || 0),
        notes: it.notes || "",
      })),
      quantity: listItems.length,
      amount: getListTotal(),
      supplier: listSupplier || null,
      urgency: listUrgency,
      notes: listNotes || "",
      requested_by: requesterName,
    };
    try {
      if (editingNeed) {
        await axios.put(`${API}/needs/${editingNeed.id}`, { ...payload, status: "en_attente" });
        toast.success("Besoin mis à jour");
      } else {
        await axios.post(`${API}/needs`, payload);
        toast.success(`Besoin créé avec ${listItems.length} article(s)`);
      }
      setShowCreateModal(false);
      resetListForm();
      fetchNeeds();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const deleteNeed = async (id) => {
    if (!confirm("Supprimer ce besoin ?")) return;
    try {
      await axios.delete(`${API}/needs/${id}`);
      toast.success("Supprimé");
      fetchNeeds();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const cancelNeed = async (id) => {
    const reason = prompt("Raison de l'annulation (optionnel) :", "");
    try {
      await axios.post(`${API}/needs/${id}/cancel`, { reason: reason || "" });
      toast.success("Besoin annulé");
      fetchNeeds();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const convertToExpense = async (id) => {
    if (!confirm("Convertir ce besoin en demande d'achats ?")) return;
    try {
      const res = await axios.post(`${API}/needs/${id}/convert-to-expense`, { category: "autres" });
      if (res.data?.success) {
        toast.success("Demande d'achats créée");
        fetchNeeds();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const markProcessed = async (id, andArchive = false) => {
    const label = andArchive ? "Marquer comme traité et archiver ?" : "Marquer comme traité ?";
    if (!confirm(label)) return;
    try {
      const res = await axios.post(`${API}/needs/${id}/mark-processed`, {
        archive: andArchive,
        actor: requesterName,
      });
      if (res.data?.success) {
        toast.success(andArchive ? "Besoin traité et archivé" : "Besoin marqué traité");
        fetchNeeds();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const archiveNeed = async (id) => {
    if (!confirm("Archiver ce besoin ? Il sera masqué de la liste active.")) return;
    try {
      await axios.post(`${API}/needs/${id}/archive`, { actor: requesterName });
      toast.success("Besoin archivé");
      fetchNeeds();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const unarchiveNeed = async (id) => {
    try {
      await axios.post(`${API}/needs/${id}/unarchive`);
      toast.success("Besoin restauré");
      fetchNeeds();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const exportNeeds = async (kind) => {
    try {
      const params = new URLSearchParams();
      if (statusTab !== "all") params.append("status", statusTab);
      if (locationFilter !== "all") params.append("location", locationFilter);
      const url = `${API}/needs/export/${kind}${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await axios.get(url, { responseType: "blob" });
      const mime = kind === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const ext = kind === "pdf" ? "pdf" : "xlsx";
      const blob = new Blob([res.data], { type: mime });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `liste_besoins_${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${ext.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error("Erreur lors de l'export");
    }
  };

  const currentTab = STATUS_TABS.find((t) => t.value === statusTab) || STATUS_TABS[0];
  const activeFilterCount = (locationFilter !== "all" ? 1 : 0);

  return (
    <div className="space-y-4" data-testid="needs-tab">
      {/* ═══════════ HERO HEADER ═══════════ */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-900/40 via-slate-900/40 to-purple-900/30 border border-indigo-500/20 p-4 sm:p-5">
        <div className="absolute -top-16 -right-16 w-48 h-48 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-12 w-40 h-40 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex items-start sm:items-center gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-indigo-500/30">
              <ClipboardList className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.4} />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-black text-white leading-tight tracking-tight">Besoins</h2>
              <p className="text-[13px] text-slate-300 leading-tight mt-0.5">
                {totalPending > 0
                  ? <>
                      <span className="text-amber-300 font-bold">{totalPending}</span>
                      <span className="text-slate-400"> en attente</span>
                      {totalPendingAmount > 0 && (
                        <>
                          <span className="text-slate-500 mx-1.5">·</span>
                          <span className="text-indigo-300 font-bold tabular-nums">{formatPrice(totalPendingAmount)}</span>
                          <span className="text-slate-500 text-[11px] ml-0.5">F</span>
                        </>
                      )}
                    </>
                  : <span className="text-slate-400">Tout est à jour</span>}
              </p>
            </div>
          </div>

          {(isManager || isAdmin) && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={openCreate}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 shadow-md shadow-indigo-500/25 h-10 px-3 sm:px-4"
                data-testid="new-need-btn"
              >
                <Plus className="w-4 h-4 sm:mr-1.5" strokeWidth={2.6} />
                <span className="hidden sm:inline font-semibold">Nouveau besoin</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="border-slate-700/60 bg-slate-800/60 text-slate-300 hover:bg-slate-700 h-10 w-10 rounded-lg backdrop-blur-sm"
                    data-testid="needs-more-menu-btn"
                    title="Actions"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 text-white">
                  <DropdownMenuLabel className="text-xs text-slate-400">Exports</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => exportNeeds("pdf")}
                    disabled={needs.length === 0}
                    className="cursor-pointer focus:bg-slate-700"
                    data-testid="export-needs-pdf"
                  >
                    <FileText className="w-4 h-4 mr-2 text-rose-400" /> Exporter en PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => exportNeeds("excel")}
                    disabled={needs.length === 0}
                    className="cursor-pointer focus:bg-slate-700"
                    data-testid="export-needs-excel"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-400" /> Exporter en Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      {/* ═══════════ SEGMENTED STATUS ═══════════ */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none">
        {STATUS_TABS.map((tab) => {
          const active = statusTab === tab.value;
          let count;
          if (tab.value === "all") count = globalCounts.en_attente + globalCounts.traite + globalCounts.annule;
          else if (tab.value === "archived") count = globalCounts.archived || 0;
          else count = globalCounts[tab.value] || 0;
          return (
            <button
              key={tab.value}
              onClick={() => setStatusTab(tab.value)}
              data-testid={`needs-tab-${tab.value}`}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-[12.5px] font-bold whitespace-nowrap transition-all ${
                active
                  ? `${tab.bg} ${tab.accent} ring-1 ${tab.ring} shadow-md`
                  : "bg-slate-800/40 text-slate-400 hover:text-white hover:bg-slate-800/70"
              }`}
            >
              {tab.label}
              <span
                className={`rounded-full px-1.5 min-w-[22px] text-center text-[10.5px] leading-[18px] tabular-nums font-black ${
                  active ? "bg-white/15" : "bg-slate-700/60"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ═══════════ FILTERS (collapsible on mobile) ═══════════ */}
      <div className="sm:hidden">
        <button
          type="button"
          onClick={() => setShowFilters((s) => !s)}
          className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-[13px] font-semibold transition-all ${
            showFilters || activeFilterCount > 0
              ? "bg-indigo-500/15 text-indigo-200 ring-1 ring-indigo-500/40"
              : "bg-slate-800/50 text-slate-300 ring-1 ring-slate-700/60"
          }`}
          data-testid="needs-filters-toggle"
        >
          <span className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4" strokeWidth={2.4} />
            Filtres
            {activeFilterCount > 0 && (
              <span className="bg-indigo-500 text-white text-[10px] font-black px-1.5 py-0.5 rounded-full min-w-[18px]">
                {activeFilterCount}
              </span>
            )}
            {activeFilterCount > 0 && (
              <span className="text-[11.5px] text-indigo-300 font-medium">· {LOC_MAP[locationFilter]?.label}</span>
            )}
          </span>
          {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      <div className={`${showFilters ? "block" : "hidden"} sm:block`}>
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-none" data-testid="needs-location-pills">
          <Filter className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
          <button
            onClick={() => setLocationFilter("all")}
            className={`px-3 py-1.5 rounded-full text-[11.5px] font-semibold whitespace-nowrap transition-all ${
              locationFilter === "all"
                ? "bg-white/10 text-white ring-1 ring-white/25"
                : "bg-slate-800/40 text-slate-400 hover:text-white hover:bg-slate-800/70"
            }`}
          >
            Tous espaces
          </button>
          {LOCATIONS.map((l) => {
            const Icon = l.icon;
            const active = locationFilter === l.value;
            return (
              <button
                key={l.value}
                onClick={() => setLocationFilter(l.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold whitespace-nowrap transition-all ${
                  active
                    ? "bg-white/10 text-white ring-1 ring-white/25"
                    : "bg-slate-800/40 text-slate-400 hover:text-white hover:bg-slate-800/70"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${l.dot}`} />
                <Icon className="w-3 h-3" />
                {l.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ===== Liste des besoins (cartes compactes) ===== */}
      {loading ? (
        <div className="text-center text-slate-400 py-10">Chargement…</div>
      ) : needs.length === 0 ? (
        <Card className={`${currentTab.bg} border-slate-700`}>
          <CardContent className="py-8 text-center">
            <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40 text-slate-300" />
            <p className="text-slate-300 text-sm">
              Aucun besoin {statusTab !== "all" && `« ${currentTab.label.toLowerCase()} »`}
            </p>
            {(isManager || isAdmin) && statusTab === "en_attente" && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3 border-slate-600 text-slate-200"
                onClick={openCreate}
              >
                <Plus className="w-4 h-4 mr-1" /> Créer le premier
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {needs.map((n) => {
            const loc = LOC_MAP[n.location] || LOC_MAP.autres;
            const Icon = loc.icon;
            const analysis = isAdmin ? analyses[n.id] : null;
            const isPending = n.status === "en_attente";
            const isTraite = n.status === "traite";
            const isCancelled = n.status === "annule";
            const isArchived = n.archived === true;
            const nbItems = (n.items || []).length;

            // Aperçu articles (les 3 premiers max) pour lecture rapide
            const itemsPreview = (n.items || []).slice(0, 3).map((it) => it.description).join(" · ");
            const hasMore = nbItems > 3;

            const locBg = (
              loc.value === "cuisine" ? "from-orange-500/25 to-orange-600/10 border-orange-500/30" :
              loc.value === "salle_jeux" ? "from-fuchsia-500/25 to-fuchsia-600/10 border-fuchsia-500/30" :
              loc.value === "salle" ? "from-blue-500/25 to-blue-600/10 border-blue-500/30" :
              loc.value === "jardin" ? "from-emerald-500/25 to-emerald-600/10 border-emerald-500/30" :
              loc.value === "toilettes" ? "from-cyan-500/25 to-cyan-600/10 border-cyan-500/30" :
              "from-slate-500/25 to-slate-600/10 border-slate-500/30"
            );

            return (
              <div
                key={n.id}
                className={`group rounded-2xl border transition-all overflow-hidden ${
                  isArchived
                    ? "bg-slate-900/60 border-slate-800 opacity-60"
                    : isPending
                    ? "bg-slate-800/70 border-slate-700/80 hover:border-indigo-500/60 hover:shadow-lg hover:shadow-indigo-500/10"
                    : isTraite
                    ? "bg-emerald-900/10 border-emerald-500/25"
                    : "bg-slate-800/30 border-slate-700/60 opacity-75"
                }`}
                data-testid={`need-card-${n.id}`}
              >
                {/* HEADER : icône couleur + titre + montant */}
                <div className="flex items-start gap-3 p-3.5 sm:p-4">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${locBg} flex items-center justify-center flex-shrink-0 border`}>
                    <Icon className="w-5 h-5 text-white" strokeWidth={2.2} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2 min-w-0">
                      <h3 className="text-white font-bold text-[14.5px] sm:text-[15px] leading-tight flex-1 line-clamp-2">{n.description}</h3>
                      {n.urgency === "urgente" && isPending && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-black uppercase tracking-wider text-rose-200 bg-rose-500/25 border border-rose-500/40 px-1.5 py-0.5 rounded flex-shrink-0">
                          <Flame className="w-2.5 h-2.5" /> Urgent
                        </span>
                      )}
                      {isTraite && !isArchived && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-black uppercase text-emerald-200 bg-emerald-500/20 border border-emerald-500/40 px-1.5 py-0.5 rounded flex-shrink-0">
                          <CheckCircle className="w-2.5 h-2.5" /> Traité
                        </span>
                      )}
                      {isCancelled && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-black uppercase text-rose-300 bg-rose-500/15 border border-rose-500/30 px-1.5 py-0.5 rounded flex-shrink-0">
                          <X className="w-2.5 h-2.5" /> Annulé
                        </span>
                      )}
                      {isArchived && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-black uppercase text-slate-400 bg-slate-500/15 border border-slate-500/30 px-1.5 py-0.5 rounded flex-shrink-0">
                          <Package className="w-2.5 h-2.5" /> Archivé
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11.5px] text-slate-400 mt-1.5 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-300 text-[10.5px] font-semibold`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${loc.dot}`} />
                        {loc.label}
                      </span>
                      <span className="text-slate-500">{n.requested_by || "—"}</span>
                      <span className="text-slate-600">·</span>
                      <span className="text-slate-500 tabular-nums">{(n.created_at || "").slice(0, 10)}</span>
                      <span className="text-slate-600">·</span>
                      <span className="text-slate-400 font-semibold">{nbItems} art.</span>
                      {n.converted_to_expense_id && (
                        <>
                          <span className="text-slate-600">·</span>
                          <span className="text-emerald-400 font-bold">converti</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <div className={`font-black text-[16px] sm:text-[17px] tabular-nums leading-none ${isPending ? "text-indigo-300" : "text-slate-400"}`}>
                      {formatPrice(n.amount)}
                      <span className="text-[10px] text-slate-500 ml-0.5 font-semibold">F</span>
                    </div>
                    {(isManager || isAdmin) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-slate-400 hover:text-white hover:bg-slate-700 -mr-1"
                            data-testid={`need-menu-${n.id}`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 text-white min-w-[200px]">
                          {isPending && (isManager || isAdmin) && (
                            <>
                              <DropdownMenuItem onClick={() => openEdit(n)} className="cursor-pointer focus:bg-slate-700" data-testid={`edit-need-${n.id}`}>
                                <Edit2 className="w-4 h-4 mr-2 text-slate-300" /> Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => deleteNeed(n.id)} className="cursor-pointer focus:bg-slate-700 text-rose-300" data-testid={`delete-need-${n.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </>
                          )}
                          {isPending && isAdmin && (
                            <>
                              <DropdownMenuSeparator className="bg-slate-700" />
                              <DropdownMenuItem onClick={() => markProcessed(n.id, false)} className="cursor-pointer focus:bg-slate-700 text-emerald-300" data-testid={`mark-processed-${n.id}`}>
                                <CheckCircle className="w-4 h-4 mr-2" /> Marquer traité
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => markProcessed(n.id, true)} className="cursor-pointer focus:bg-slate-700 text-emerald-200" data-testid={`mark-processed-archive-${n.id}`}>
                                <CheckCircle className="w-4 h-4 mr-2" /> Traité & archiver
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => cancelNeed(n.id)} className="cursor-pointer focus:bg-slate-700 text-slate-300" data-testid={`cancel-need-${n.id}`}>
                                <X className="w-4 h-4 mr-2" /> Annuler
                              </DropdownMenuItem>
                            </>
                          )}
                          {!isPending && isAdmin && !isArchived && (
                            <>
                              <DropdownMenuItem onClick={() => archiveNeed(n.id)} className="cursor-pointer focus:bg-slate-700 text-slate-200" data-testid={`archive-need-${n.id}`}>
                                <Package className="w-4 h-4 mr-2" /> Archiver
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => deleteNeed(n.id)} className="cursor-pointer focus:bg-slate-700 text-rose-300" data-testid={`admin-delete-need-${n.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement
                              </DropdownMenuItem>
                            </>
                          )}
                          {isArchived && isAdmin && (
                            <>
                              <DropdownMenuItem onClick={() => unarchiveNeed(n.id)} className="cursor-pointer focus:bg-slate-700 text-emerald-300" data-testid={`unarchive-need-${n.id}`}>
                                <ArrowRightCircle className="w-4 h-4 mr-2" /> Restaurer
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => deleteNeed(n.id)} className="cursor-pointer focus:bg-slate-700 text-rose-300" data-testid={`admin-delete-need-${n.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer définitivement
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {/* Aperçu articles */}
                {itemsPreview && (
                  <div className="px-4 pb-2.5 -mt-1">
                    <p className="text-[12px] text-slate-400 leading-relaxed">
                      <span className="text-slate-500 font-bold mr-1">›</span>
                      {itemsPreview}
                      {hasMore && <span className="text-indigo-400 font-semibold"> · +{nbItems - 3} autres</span>}
                    </p>
                  </div>
                )}

                {/* Notes */}
                {n.notes && (
                  <div className="mx-4 mb-2.5 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[12px] text-amber-100/90 italic leading-relaxed">
                    « {n.notes} »
                  </div>
                )}

                {/* Analyses admin */}
                {analysis && isPending && (
                  <div className="px-4 pb-2.5 border-t border-slate-700/40 pt-2.5">
                    <ExpenseAnalysisBadges analysis={analysis} />
                  </div>
                )}

                {/* Actions PRIMAIRES admin */}
                {isPending && isAdmin && (
                  <div className="grid grid-cols-2 gap-2 p-3 pt-2 border-t border-slate-700/40">
                    <Button
                      size="sm"
                      onClick={() => convertToExpense(n.id)}
                      className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white text-[12.5px] font-semibold shadow-md shadow-emerald-500/20"
                      data-testid={`convert-need-${n.id}`}
                    >
                      <ArrowRightCircle className="w-4 h-4 mr-1.5" strokeWidth={2.4} /> Convertir
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setStockDialogNeed(n)}
                      className="h-9 bg-sky-600 hover:bg-sky-700 text-white text-[12.5px] font-semibold shadow-md shadow-sky-500/20"
                      data-testid={`need-to-stock-${n.id}`}
                      title="Ajouter directement les articles au stock"
                    >
                      <Warehouse className="w-4 h-4 mr-1.5" strokeWidth={2.4} /> Vers stock
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ===== Modale Création / Edition (inchangée fonctionnellement, style resserré) ===== */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        if (!open && listItems.length > 0 && !editingNeed) {
          if (!confirm("Articles non enregistrés. Fermer quand même ?")) return;
        }
        setShowCreateModal(open);
        if (!open) resetListForm();
      }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-indigo-300 flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              {editingNeed ? "Modifier le besoin" : "Nouveau besoin"}
              {listItems.length > 0 && (
                <Badge className="bg-indigo-500/30 text-indigo-200 ml-2">
                  {listItems.length} article(s) · {formatPrice(getListTotal())} F
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Ajoutez les articles nécessaires. Le prix est optionnel — l'admin pourra convertir en demande d'achats.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Réglages globaux */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 bg-slate-700/30 rounded-lg p-3">
              <div>
                <Label className="text-slate-300 text-xs">Espace</Label>
                <Select value={listLocation} onValueChange={setListLocation}>
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white h-9" data-testid="need-location-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {LOCATIONS.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300 text-xs">Urgence</Label>
                <Select value={listUrgency} onValueChange={setListUrgency}>
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="urgente">🔥 Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-300 text-xs">Libellé (optionnel)</Label>
                <Input
                  value={listDescription}
                  onChange={(e) => setListDescription(e.target.value)}
                  placeholder="Ex : Besoins cuisine semaine 18"
                  className="bg-slate-700/50 border-slate-600 text-white h-9"
                  data-testid="need-description-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-300 text-xs">Fournisseur pressenti (optionnel)</Label>
                <Input
                  value={listSupplier}
                  onChange={(e) => setListSupplier(e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white h-9"
                />
              </div>
            </div>

            {/* Ajouter un article */}
            <Card className="bg-indigo-900/20 border-indigo-500/30">
              <CardHeader className="pb-2 pt-3 px-3">
                <CardTitle className="text-indigo-300 text-xs flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> Ajouter un article
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-3 pb-3">
                <div className="flex flex-col sm:flex-row gap-1.5 flex-wrap">
                  <Select value={newItem.location} onValueChange={(v) => setNewItem({ ...newItem, location: v })}>
                    <SelectTrigger className="w-full sm:w-[120px] bg-slate-700/50 border-slate-600 text-white h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {LOCATIONS.map((l) => (
                        <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Article / besoin"
                    className="flex-1 min-w-[140px] bg-slate-700/50 border-slate-600 text-white h-9"
                    data-testid="new-item-desc"
                  />
                  <Input
                    type="number" min="0" step="any"
                    value={newItem.quantity || ""}
                    onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value.replace(',', '.')) || 1 })}
                    placeholder="Qté"
                    className="w-full sm:w-[70px] bg-slate-700/50 border-slate-600 text-white h-9"
                  />
                  <Input
                    type="number"
                    value={newItem.unit_price || ""}
                    onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                    placeholder="Prix opt."
                    className="w-full sm:w-[100px] bg-slate-700/50 border-slate-600 text-white h-9"
                  />
                  <Button onClick={addItemToList} className="bg-indigo-600 hover:bg-indigo-700 h-9" data-testid="add-item-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Liste des articles ajoutés */}
            {listItems.length > 0 && (
              <Card className="bg-slate-700/30 border-slate-600">
                <CardHeader className="pb-1.5 pt-2.5 px-3">
                  <CardTitle className="text-slate-300 text-xs flex items-center justify-between">
                    <span>Articles ({listItems.length})</span>
                    <span className="text-indigo-300 font-semibold">{formatPrice(getListTotal())} F</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 px-3 pb-3 space-y-1 max-h-[220px] overflow-y-auto">
                  {listItems.map((it, idx) => {
                    const locItem = LOC_MAP[it.location] || LOC_MAP.autres;
                    return (
                      <div key={it.id} className="flex items-center justify-between gap-2 bg-slate-600/30 rounded p-1.5 text-xs">
                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                          <span className="text-slate-500 font-mono text-[10px]">{idx + 1}.</span>
                          <span className={`w-1.5 h-1.5 rounded-full ${locItem.dot} flex-shrink-0`} />
                          <span className="text-white truncate">{it.description}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-slate-400">
                            {it.quantity} × {formatPrice(it.unit_price)}{" "}
                            <span className="text-slate-500">= {formatPrice(it.amount)} F</span>
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeFromList(it.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-6 w-6 p-0"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            <div>
              <Label className="text-slate-300 text-xs">Notes (optionnel)</Label>
              <Textarea
                value={listNotes}
                onChange={(e) => setListNotes(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white min-h-[50px] text-sm"
                placeholder="Précisions, contexte, deadline…"
              />
            </div>

            <div className="flex gap-2 pt-1">
              <Button
                onClick={saveNeed}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 h-10"
                data-testid="save-need-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {editingNeed ? "Enregistrer" : `Soumettre ${listItems.length} article(s)`}
              </Button>
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-slate-600 text-slate-300 h-10">
                Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale Admin : passer directement en stock */}
      <NeedToStockDialog
        open={!!stockDialogNeed}
        need={stockDialogNeed}
        onClose={() => setStockDialogNeed(null)}
        onSuccess={() => { setStockDialogNeed(null); fetchNeeds(); }}
        actorName={requesterName}
      />
    </div>
  );
};

export default NeedsTab;
